/**
 * Journal container (C24, B4 D06 part 1 — D-091 – D-096, D-221; D-251 – D-253): binds `Journal` (packages/ui, old
 * App.tsx:28643-30206) to the ledger view `GET /journal` and opens the details window, the edit window and the cancel
 * dialogs.
 *
 *   list     : the server filters, sorts and pages (office dates / hour, D-096); the default range is the office's today.
 *   exports  : share / save PDF / print take the whole filtered set (D-095) with totals per currency, printed as the
 *              old A4 report (`p07`, StatementA4) through the browser until the PDF worker of B5.
 *   cancel   : one row or the selection → reason (+ the batch summary, D-094) → own PIN → reversal entries; a balance
 *              below zero → the Super Manager's approval (D-014) → own PIN again.
 *   edit     : the editable business inputs listed by the server (D-092) → preview (before / after figures from the
 *              server) → own PIN (+ D-014 approval when the preview reports a negative balance) → commit.
 * Labels are formatted here; no money is computed (display sums of decimal strings only, as the other containers).
 */
import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { PRINT_ROOT_CLASS, StatementA4, printDocument } from "@faylasuf/print";
import {
  Journal, JournalCancelDialog, MovementDetailsModal, MovementEditModal, NegativeApprovalDialog, formatWithCommas,
  type EditCategory, type EditFieldView, type EditPreviewView, type JournalMode, type JournalRowView, type JournalSortField, type JournalTypeFilter, type MovementDetailsView,
} from "@faylasuf/ui";
import type { Translate } from "@faylasuf/i18n";
import type { JournalDetail, JournalEditPreview, JournalList, JournalNegativeOverride, JournalQuery, JournalRowData, Money } from "../api/types.js";
import { useApp, useSession } from "../app/context.js";
import { engineErrors, errorMessage, isSessionLost } from "../app/errors.js";
import { usePinGate } from "../app/pin-gate.js";
import { keys } from "../app/query.js";
import { useToast } from "../app/toast.js";
import { decAdd, decSub, fmt, isZero } from "./customers/format.js";
import { officeDate } from "./wallets/StatementContainer.js";
import { usePrintOffice } from "../app/printOffice.js";

const SYMBOL: Readonly<Record<string, string>> = { IQD: "", USD: "$" };
const CURRENCY_ORDER = (a: string, b: string) => (a === "IQD" ? -1 : b === "IQD" ? 1 : a === "USD" ? -1 : b === "USD" ? 1 : a.localeCompare(b));

/** "3,003,000" / "2,000 $" / "3,000 + 10 $"; `empty` when nothing moved. */
export function moneyLabel(m: Money, empty: string): string {
  const parts = Object.keys(m)
    .sort(CURRENCY_ORDER)
    .filter((c) => !isZero(m[c]!))
    .map((c) => (c === "IQD" ? fmt(m[c]!) : `${fmt(m[c]!)} ${SYMBOL[c] ?? c}`));
  return parts.length > 0 ? parts.join(" + ") : empty;
}
const iqdOnly = (m: Money): string => fmt(m["IQD"] ?? "0");
/** "2026-09-29" + "08:57" → "09-29-2026 08:57" (old en-US date with dashes, D-096 office time zone). */
const dateLabel = (officeDay: string, time: string): string => {
  const [y, mo, d] = officeDay.split("-");
  return `${mo}-${d}-${y} ${time}`;
};
const rateLabel = (v: string | null): string => (v ? formatWithCommas(v) : "0");

/** The row's description: `jr.item.<type>` with the operation's parameters (every placeholder filled). */
export function itemOf(t: Translate, type: string, params: Readonly<Record<string, string>>): string {
  const p: Record<string, string> = { quote: "", saleQuote: "", rate: "", usdRate: "", usd: "", qty: "", amount: "", item: "", party: "", box: "", from: "", to: "", code: "", beneficiaryName: "", destination: "" };
  for (const [k, v] of Object.entries(params)) p[k] = ["quote", "saleQuote", "rate", "usdRate", "usd", "qty", "amount", "profit"].includes(k) ? formatWithCommas(v) : v;
  p["currencyName"] = params["currency"] ?? "";
  p["categoryName"] = params["category"] ? t(`jr.expense_cat.${params["category"]}`) : "";
  const text = t(`jr.item.${type}`, p);
  return text.replace(/\s+-\s*$/, "").replace(/\(\s*(كود|code):\s*\)/, "").trim();
}

function setPath(obj: Readonly<Record<string, unknown>>, path: string, value: string): Record<string, unknown> {
  const [head, ...rest] = path.split(".");
  const copy: Record<string, unknown> = { ...obj };
  if (rest.length === 0) copy[head!] = value;
  else copy[head!] = setPath((obj[head!] ?? {}) as Record<string, unknown>, rest.join("."), value);
  return copy;
}

function categoryOf(type: string): EditCategory {
  if (type.startsWith("usd_") || type.startsWith("currency_") || type === "account_conversion") return "usd";
  if (type === "box_transfer" || type === "custody_transfer") return "transfer";
  if (type.startsWith("customer_") || type === "partner_charge") return "debt";
  return "general";
}

interface CancelTarget {
  readonly ids: readonly string[];
  readonly codeLabel: string | null;
}

export function JournalPage() {
  const { t, locale, source } = useApp();
  const { session, hasPermission, isSuperManager, sessionLost } = useSession();
  const printOffice = usePrintOffice(); // [C29] F-286: identity lines in the A4 header
  const toast = useToast();
  const qc = useQueryClient();
  const pin = usePinGate();
  const tz = session.office.timeZone;
  const today = officeDate(new Date(), tz);

  const [mode, setMode] = useState<JournalMode>("journal");
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState<JournalTypeFilter>("all");
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [hour, setHour] = useState("all");
  const [sortField, setSortField] = useState<JournalSortField>("created_at");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [selected, setSelected] = useState<string[]>([]);
  const [employee, setEmployee] = useState("");
  const [empType, setEmpType] = useState<JournalTypeFilter>("all");
  const [empStart, setEmpStart] = useState(today);
  const [empEnd, setEmpEnd] = useState(today);

  const query: JournalQuery = {
    ...(startDate ? { from: startDate } : {}),
    ...(endDate ? { to: endDate } : {}),
    ...(hour !== "all" ? { hour } : {}),
    category: typeFilter,
    ...(search.trim() ? { search: search.trim() } : {}),
    sort: sortField,
    order: sortOrder,
    page,
    pageSize,
  };
  const q = useQuery({ queryKey: [...keys.journal, "list", query], queryFn: () => source.journal(query), staleTime: 5_000 });
  const empQuery: JournalQuery = { ...(empStart ? { from: empStart } : {}), ...(empEnd ? { to: empEnd } : {}), category: empType, ...(employee ? { operatorUserId: employee } : {}), all: true };
  const eq = useQuery({ queryKey: [...keys.journal, "employee", empQuery], queryFn: () => source.journal(empQuery), enabled: mode === "employee", staleTime: 5_000 });
  if ((q.error && isSessionLost(q.error)) || (eq.error && isSessionLost(eq.error))) sessionLost();
  const data: JournalList | undefined = q.data;

  // filters changing → back to page 1, selection kept (old: selection survived paging)
  useEffect(() => setPage(1), [startDate, endDate, hour, typeFilter, search, pageSize]);

  const toView = (r: JournalRowData): JournalRowView => ({
    id: r.id,
    code: r.code,
    item: itemOf(t, r.type, r.params),
    deferred: r.deferred,
    partyName: r.partyName,
    note: r.note,
    boxName: r.boxName,
    operator: r.operator.username,
    costLabel: rateLabel(r.costPrice),
    saleLabel: rateLabel(r.salePrice),
    inLabel: moneyLabel(r.in, "-"),
    outLabel: moneyLabel(r.out, "-"),
    commLabel: moneyLabel(r.comm, "0"),
    dateLabel: dateLabel(r.officeDate, r.time),
    edited: r.edited,
    pending: r.pendingAction,
  });
  const rows = useMemo(() => (data?.rows ?? []).map(toView), [data, t]);
  const empRows = useMemo(() => (eq.data?.rows ?? []).map(toView), [eq.data, t]);

  const invalidate = () => {
    for (const k of [keys.journal, keys.accounts, keys.dashboard, keys.currencies, keys.dollar, keys.transfers, keys.profits, keys.capitalOperations]) void qc.invalidateQueries({ queryKey: k });
    void qc.invalidateQueries({ queryKey: ["customers"] });
  };
  const fail = (e: unknown) => {
    if (isSessionLost(e)) sessionLost();
    else toast(errorMessage(t, e), "error");
  };

  // ---------------------------------------------------------------- D-014: negative balances need the Super Manager
  const [negative, setNegative] = useState<{ accounts: string[]; ids: string[]; resolve: (o: JournalNegativeOverride | null) => void } | null>(null);
  const askNegative = (accounts: { id: string; label: string }[]): Promise<JournalNegativeOverride | null> =>
    new Promise((resolve) => setNegative({ accounts: accounts.map((a) => a.label), ids: accounts.map((a) => a.id), resolve }));
  const negativeAccounts = async (e: unknown, operationIds: readonly string[]): Promise<{ id: string; label: string }[] | null> => {
    const list = engineErrors(e).filter((x) => x.code === "NEGATIVE_BALANCE_APPROVAL_REQUIRED");
    if (list.length === 0 || list.length !== engineErrors(e).length) return null;
    const names = new Map<string, string>();
    for (const id of operationIds) {
      try {
        const d = await source.journalDetail(id);
        for (const l of d.lines) if (l.name) names.set(l.accountId, l.name);
      } catch {
        /* names are a convenience */
      }
    }
    return list.map((x) => ({ id: String(x.params["account"]), label: `${names.get(String(x.params["account"])) ?? String(x.params["account"])}: ${fmt(String(x.params["balance"] ?? "0"))}` }));
  };

  // ---------------------------------------------------------------- cancel / bulk cancel (D-091, D-094)
  const [cancelTarget, setCancelTarget] = useState<CancelTarget | null>(null);
  const [busy, setBusy] = useState(false);
  const allRows = data?.rows ?? [];
  const cancelSummary = (() => {
    if (!cancelTarget || cancelTarget.ids.length < 2) return [];
    const picked = allRows.filter((r) => cancelTarget.ids.includes(r.id));
    const totIn: Record<string, string> = {};
    const totOut: Record<string, string> = {};
    for (const r of picked) {
      for (const [c, v] of Object.entries(r.in)) totIn[c] = decAdd(totIn[c] ?? "0", v);
      for (const [c, v] of Object.entries(r.out)) totOut[c] = decAdd(totOut[c] ?? "0", v);
    }
    const out = [{ label: t("jr.cx_count"), value: String(cancelTarget.ids.length) }];
    for (const c of Object.keys(totIn).sort(CURRENCY_ORDER)) out.push({ label: t("jr.cx_total_in", { cur: c }), value: fmt(totIn[c]!) });
    for (const c of Object.keys(totOut).sort(CURRENCY_ORDER)) out.push({ label: t("jr.cx_total_out", { cur: c }), value: fmt(totOut[c]!) });
    return out;
  })();
  const confirmCancel = async (reason: string) => {
    if (!cancelTarget) return;
    const ids = cancelTarget.ids;
    setBusy(true);
    try {
      let override: JournalNegativeOverride = {};
      for (;;) {
        try {
          const res = await pin.withPin((proof) => source.journalCancel({ operationIds: ids, reason, idempotencyKey: crypto.randomUUID(), ...override }, proof.pin ?? ""), { message: t("jr.pin_cancel", { n: ids.length }), allowSupervisor: false });
          if (!res) return;
          toast(res.status === "pending" ? t("jr.toast_pending") : t("jr.toast_cancelled"), "success");
          setCancelTarget(null);
          setSelected((s) => s.filter((x) => !ids.includes(x)));
          invalidate();
          return;
        } catch (e) {
          const acc = override.allowNegative ? null : await negativeAccounts(e, ids);
          if (!acc) throw e;
          const o = await askNegative(acc);
          if (!o) return;
          override = o;
        }
      }
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  };

  // ---------------------------------------------------------------- details
  const [detailId, setDetailId] = useState<string | null>(null);
  const dq = useQuery({ queryKey: [...keys.journal, "detail", detailId], queryFn: () => source.journalDetail(detailId!), enabled: detailId !== null });
  const [printingDetail, setPrintingDetail] = useState(false);
  const detailView = (d: JournalDetail): MovementDetailsView => {
    const fx = Object.keys({ ...d.in, ...d.out }).find((c) => c !== "IQD") ?? null;
    const typeTitle = t(`jr.type.${d.type}`);
    const time = new Date(d.postedAt).toLocaleTimeString("en-GB", { timeZone: tz, hour: "2-digit", minute: "2-digit", hour12: false });
    const params: MovementDetailsView["params"][number][] = [];
    if (d.transfer) params.push({ label: t("jr.dt_transfer_code"), value: d.transfer.code });
    if (d.costPrice) params.push({ label: t("costPrice"), value: rateLabel(d.costPrice), tone: "amber" });
    if (d.salePrice) params.push({ label: t("salePrice"), value: rateLabel(d.salePrice), tone: "amber" });
    for (const l of d.lines) {
      if (!["cash_vault", "cash_box", "digital_box", "custody", "customer_debt", "customer_trust", "partner_debt", "partner_trust", "currency_position"].includes(l.kind)) continue;
      const n = l.natural;
      params.push({ label: l.name ?? l.kind, value: `${n.startsWith("-") ? "-" : "+"}${fmt(n.replace(/^-/, ""))} ${l.currency === "IQD" ? "د.ع" : l.currency === "USD" ? "$" : l.currency}`, tone: n.startsWith("-") ? "out" : "in" });
    }
    const inIqd = d.in["IQD"] ?? "0";
    const outIqd = d.out["IQD"] ?? "0";
    const dirIn = !isZero(inIqd) && isZero(outIqd);
    const dirOut = isZero(inIqd) && !isZero(outIqd);
    params.push({ label: t("jr.dt_direction"), value: dirIn ? t("jr.dt_dir_in") : dirOut ? t("jr.dt_dir_out") : t("jr.dt_dir_adj"), ...(dirIn ? { tone: "in" as const } : dirOut ? { tone: "out" as const } : {}) });
    const totalIn = moneyLabel(d.in, "");
    const totalOut = moneyLabel(d.out, "");
    const histLabel: Record<string, string> = { original: t("jr.hist_original"), cancel: t("jr.hist_cancel"), edit_reversal: t("jr.hist_edit_reversal"), corrected: t("jr.hist_corrected"), this: t("jr.hist_this") };
    return {
      typeTitle,
      badgeColor: "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800",
      icon: d.type.startsWith("currency_") || d.type.startsWith("usd_") ? "coins" : d.type.startsWith("transfer_") ? "transfer" : "file",
      code: d.code,
      dateLabel: dateLabel(d.officeDate, "").trim(),
      timeLabel: time,
      item: itemOf(t, d.type, d.params),
      note: d.note,
      impactText: null,
      currencyBadge: d.params["currency"] && d.params["currency"] !== "IQD" ? d.params["currency"] : null,
      total: totalIn ? { label: `${totalIn} د.ع`.replace(/ \$ د\.ع$/, " $"), direction: "in" } : totalOut ? { label: `${totalOut} د.ع`.replace(/ \$ د\.ع$/, " $"), direction: "out" } : { label: "0 د.ع", direction: null },
      boxName: d.boxName ?? t("jr.vault"),
      operator: d.operator ?? t("jr.unknown"),
      commissionLabel: Object.keys(d.comm).length > 0 ? `${moneyLabel(d.comm, "0")} د.ع`.replace(/ \$ د\.ع$/, " $") : null,
      deferred: d.party !== null,
      params,
      party: d.party ? { name: d.party.name, kind: d.party.kind === "trust" ? "trust" : "debt", accountNumber: d.party.accountNumber, iqdLabel: d.party.iqd !== null ? fmt(d.party.iqd) : null, usdLabel: d.party.usd && !isZero(d.party.usd) ? fmt(d.party.usd) : null } : null,
      impact: {
        inLabel: isZero(inIqd) ? "-" : fmt(inIqd),
        outLabel: isZero(outIqd) ? "-" : fmt(outIqd),
        commLabel: isZero(d.comm["IQD"] ?? "0") ? "-" : fmt(d.comm["IQD"]!),
        fxTitle: fx && fx !== "USD" ? t("jr.dt_fx_cur", { code: fx }) : t("jr.dt_fx_usd"),
        fxLabel: fx ? `${!isZero(d.in[fx] ?? "0") ? "+" : "-"}${fmt((d.in[fx] ?? d.out[fx] ?? "0").replace(/^-/, ""))} ${SYMBOL[fx] || fx}` : "-",
      },
      history: d.history.map((h) => ({ label: histLabel[h.kind] ?? h.kind, at: `${dateLabel(officeDate(new Date(h.at), tz), new Date(h.at).toLocaleTimeString("en-GB", { timeZone: tz, hour: "2-digit", minute: "2-digit", hour12: false }))}`, actor: h.actor ?? "", reason: h.reason })),
      statusBadge: d.state.cancelled ? t("jr.dt_status_cancelled") : d.state.edited ? t("jr.dt_status_edited") : null,
    };
  };
  useEffect(() => {
    if (!printingDetail) return;
    const done = () => setPrintingDetail(false);
    window.addEventListener("afterprint", done, { once: true });
    const h = setTimeout(() => printDocument("professional-print-template"), 80);
    return () => {
      clearTimeout(h);
      window.removeEventListener("afterprint", done);
    };
  }, [printingDetail]);

  // ---------------------------------------------------------------- edit (D-092)
  const [editId, setEditId] = useState<string | null>(null);
  const editQ = useQuery({ queryKey: [...keys.journal, "detail", editId], queryFn: () => source.journalDetail(editId!), enabled: editId !== null });
  const [editValues, setEditValues] = useState<Record<string, string>>({});
  const [editReason, setEditReason] = useState("");
  const [editPreview, setEditPreview] = useState<JournalEditPreview | null>(null);
  const [editError, setEditError] = useState<string | null>(null);
  const openEdit = (id: string) => {
    setEditId(id);
    setEditValues({});
    setEditReason("");
    setEditPreview(null);
    setEditError(null);
  };
  const ed = editQ.data;
  const editFields: EditFieldView[] = (ed?.editable ?? []).map((f) => ({
    path: f.path,
    kind: f.kind,
    label: t(`jr.field.${f.key}`),
    value: editValues[f.path] ?? f.value,
    ...(f.options ? { options: f.options } : {}),
  }));
  const submitEdit = async () => {
    if (!ed) return;
    setBusy(true);
    setEditError(null);
    try {
      if (!editPreview) {
        let input: Record<string, unknown> = { ...ed.input };
        for (const [path, value] of Object.entries(editValues)) input = setPath(input, path, value);
        setEditPreview(await source.journalEditPreview(ed.id, input, editReason.trim()));
        return;
      }
      let override: JournalNegativeOverride = {};
      if (editPreview.negative.length > 0) {
        const names = new Map(ed.lines.map((l) => [l.accountId, l.name ?? l.accountId]));
        const o = await askNegative(editPreview.negative.map((id) => ({ id, label: names.get(id) ?? id })));
        if (!o) return;
        override = o;
      }
      const res = await pin.withPin((proof) => source.journalEditCommit(editPreview.previewId, crypto.randomUUID(), override, proof.pin ?? ""), { message: t("jr.pin_edit", { code: ed.code }), allowSupervisor: false });
      if (!res) return;
      toast(res.status === "pending" ? t("jr.toast_pending") : t("jr.toast_edited"), "success");
      setEditId(null);
      invalidate();
    } catch (e) {
      if (isSessionLost(e)) sessionLost();
      else setEditError(errorMessage(t, e));
      setEditPreview(null);
    } finally {
      setBusy(false);
    }
  };
  const previewView: EditPreviewView | null = editPreview
    ? {
        before: { inLabel: moneyLabel(editPreview.before.in, "-"), outLabel: moneyLabel(editPreview.before.out, "-"), commLabel: moneyLabel(editPreview.before.comm, "0") },
        after: { inLabel: moneyLabel(editPreview.after.in, "-"), outLabel: moneyLabel(editPreview.after.out, "-"), commLabel: moneyLabel(editPreview.after.comm, "0") },
        negative: editPreview.negative.map((id) => ed?.lines.find((l) => l.accountId === id)?.name ?? id),
        requiresApproval: editPreview.requiresApproval,
      }
    : null;

  // ---------------------------------------------------------------- exports (D-095: the whole filtered set)
  const [report, setReport] = useState<{ title: string; customerName: string; list: JournalList } | null>(null);
  const runReport = async (emp: boolean) => {
    try {
      const list = emp ? eq.data ?? (await source.journal(empQuery)) : await source.journal({ ...query, all: true });
      let title: string;
      let customerName: string;
      if (emp) {
        const u = data?.operators.find((o) => o.id === employee) ?? eq.data?.operators.find((o) => o.id === employee);
        const name = u ? `${u.username}${u.employeeCode ? ` (${u.employeeCode})` : ""}` : t("jr.emp_all");
        title = t("jr.report_emp_title", { name });
        customerName = name;
      } else {
        title = search.trim() ? t("jr.report_title_customer", { q: search.trim() }) : t("jr.report_title");
        // [C29] the print path of the old journal passed the search term only (App.tsx:29119 handleDirectPrint(…, searchTerm)):
        // the header badge shows the title, not "تقرير عام" (that label belonged to the WhatsApp share, App.tsx:28898-28902)
        customerName = search.trim();
      }
      setReport({ title, customerName, list });
    } catch (e) {
      fail(e);
    }
  };
  useEffect(() => {
    if (!report) return;
    const done = () => setReport(null);
    window.addEventListener("afterprint", done, { once: true });
    const h = setTimeout(() => printDocument("professional-print-template"), 80);
    return () => {
      clearTimeout(h);
      window.removeEventListener("afterprint", done);
    };
  }, [report]);
  const reportSummary = (l: JournalList) => {
    const out: { label: string; value: string }[] = [{ label: t("jr.report_count"), value: String(l.count) }];
    const curs = Array.from(new Set([...Object.keys(l.totals.in), ...Object.keys(l.totals.out), ...Object.keys(l.totals.comm)])).sort(CURRENCY_ORDER);
    for (const c of curs.length > 0 ? curs : ["IQD"]) {
      const suffix = c === "IQD" ? "" : ` (${c})`;
      const i = l.totals.in[c] ?? "0";
      const o = l.totals.out[c] ?? "0";
      out.push({ label: `${t("jr.report_total_in")}${suffix}`, value: fmt(i) });
      out.push({ label: `${t("jr.report_total_out")}${suffix}`, value: fmt(o) });
      out.push({ label: `${t("jr.report_total_comm")}${suffix}`, value: fmt(l.totals.comm[c] ?? "0") });
      out.push({ label: `${t("jr.report_net")}${suffix}`, value: fmt(decSub(i, o)) });
    }
    return out;
  };

  // employee summary cards (old :30000-30054: IQD figures)
  const empTotals = eq.data?.totals ?? { in: {}, out: {}, comm: {} };
  const employeeOptions = (data?.operators ?? []).map((u) => ({ id: u.id, label: u.username, badge: u.manager ? t("jr.emp_badge_manager") : t("jr.emp_badge_staff") }));

  const detail = dq.data;
  return (
    <>
      <Journal
        mode={mode}
        onModeChange={setMode}
        showEmployeeMode={hasPermission("view_movements_by_employee")}
        search={search}
        onSearchChange={setSearch}
        typeFilter={typeFilter}
        onTypeFilterChange={setTypeFilter}
        startDate={startDate}
        onStartDateChange={setStartDate}
        endDate={endDate}
        onEndDateChange={setEndDate}
        hourFilter={hour}
        onHourFilterChange={setHour}
        onReset={() => {
          setSearch("");
          setStartDate(today);
          setEndDate(today);
          setTypeFilter("all");
          setHour("all");
          setSelected([]);
        }}
        onShare={() => void runReport(false)}
        onSavePdf={() => void runReport(false)}
        onPrint={() => void runReport(false)}
        rows={rows}
        selectedIds={selected}
        onSelectionChange={setSelected}
        sortField={sortField}
        sortOrder={sortOrder}
        onSort={(f) => {
          if (f === sortField) setSortOrder(sortOrder === "asc" ? "desc" : "asc");
          else {
            setSortField(f);
            setSortOrder("desc");
          }
        }}
        canEdit={hasPermission("edit_movements")}
        canCancel={hasPermission("delete_movements")}
        onDetails={(id) => setDetailId(id)}
        onEdit={openEdit}
        onCancel={(id) => {
          const r = allRows.find((x) => x.id === id);
          setCancelTarget({ ids: [id], codeLabel: r ? `#${r.code}` : null });
        }}
        onBulkCancel={() => setCancelTarget({ ids: selected, codeLabel: null })}
        page={data?.page ?? 1}
        totalPages={data?.pages ?? 1}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={(n) => setPageSize(n)}
        employeeOptions={employeeOptions}
        selectedEmployee={employee}
        onEmployeeChange={setEmployee}
        empTypeFilter={empType}
        onEmpTypeFilterChange={setEmpType}
        empStartDate={empStart}
        onEmpStartDateChange={setEmpStart}
        empEndDate={empEnd}
        onEmpEndDateChange={setEmpEnd}
        onEmpReset={() => {
          setEmployee("");
          setEmpStart(today);
          setEmpEnd(today);
          setEmpType("all");
        }}
        onEmpShare={() => void runReport(true)}
        onEmpSavePdf={() => void runReport(true)}
        onEmpPrint={() => void runReport(true)}
        empStats={{ count: String(eq.data?.count ?? 0), inLabel: iqdOnly(empTotals.in), outLabel: iqdOnly(empTotals.out), commLabel: iqdOnly(empTotals.comm) }}
        empRows={empRows}
      />
      <MovementDetailsModal
        open={detailId !== null && detail !== undefined}
        view={detail ? detailView(detail) : null}
        canEdit={hasPermission("edit_movements") && (detail?.editable.length ?? 0) > 0}
        onEdit={() => {
          const id = detailId;
          setDetailId(null);
          if (id) openEdit(id);
        }}
        onPrint={() => setPrintingDetail(true)}
        onClose={() => setDetailId(null)}
      />
      <MovementEditModal
        open={editId !== null && ed !== undefined}
        idLabel={ed?.code ?? ""}
        category={categoryOf(ed?.type ?? "")}
        item={ed ? itemOf(t, ed.type, ed.params) : ""}
        operator={ed?.operator ?? ""}
        fields={editFields}
        onFieldChange={(path, value) => {
          setEditValues((v) => ({ ...v, [path]: value }));
          setEditPreview(null);
        }}
        reason={editReason}
        onReasonChange={(v) => {
          setEditReason(v);
          setEditPreview(null);
        }}
        preview={previewView}
        error={editError ?? (ed && ed.editable.length === 0 ? t("jr.ed_none_editable") : null)}
        busy={busy}
        onSubmit={() => void submitEdit()}
        onClose={() => setEditId(null)}
      />
      <JournalCancelDialog
        open={cancelTarget !== null}
        count={cancelTarget?.ids.length ?? 0}
        codeLabel={cancelTarget?.codeLabel ?? null}
        summary={cancelSummary}
        busy={busy}
        onConfirm={(r) => void confirmCancel(r)}
        onClose={() => setCancelTarget(null)}
      />
      <NegativeApprovalDialog
        open={negative !== null}
        accounts={negative?.accounts ?? []}
        selfApproval={isSuperManager}
        busy={false}
        onConfirm={(approval) => {
          const n = negative;
          setNegative(null);
          n?.resolve({ allowNegative: n.ids, ...(approval ? { negativeApproval: approval } : {}) });
        }}
        onClose={() => {
          const n = negative;
          setNegative(null);
          n?.resolve(null);
        }}
      />
      <div className="relative z-[130]">{pin.dialog}</div>
      {report &&
        createPortal(
          <div className={PRINT_ROOT_CLASS} data-testid="print-root">
            <StatementA4
              locale={locale}
              title={report.title}
              customerName={report.customerName}
              dateLabel={new Date().toLocaleDateString(locale === "ar" ? "ar-IQ" : "en-US", { timeZone: tz })}
              docNumber={String(report.list.count)}
              office={printOffice}
              columns={[
                { header: t("jr.report_col_date"), key: "date", numeric: true },
                { header: t("jr.report_col_item"), key: "item" },
                { header: t("jr.report_col_in"), key: "in", numeric: true },
                { header: t("jr.report_col_out"), key: "out", numeric: true },
                { header: t("jr.report_col_comm"), key: "comm", numeric: true },
              ]}
              rows={report.list.rows.map((r) => {
                const [y, mo, d] = r.officeDate.split("-");
                return { date: `${d}-${mo}-${y}`, item: itemOf(t, r.type, r.params), in: moneyLabel(r.in, "-"), out: moneyLabel(r.out, "-"), comm: moneyLabel(r.comm, "0") };
              })}
              summary={reportSummary(report.list)}
            />
          </div>,
          document.body,
        )}
      {printingDetail &&
        detail &&
        createPortal(
          <div className={PRINT_ROOT_CLASS} data-testid="print-root">
            <StatementA4
              locale={locale}
              title={`${t("jr.details_title")} #${detail.code}`}
              customerName={detail.party?.name ?? itemOf(t, detail.type, detail.params)}
              dateLabel={dateLabel(detail.officeDate, "").trim()}
              docNumber={detail.code}
              office={printOffice}
              columns={[
                { header: t("jr.report_col_item"), key: "account" },
                { header: t("jr.report_col_in"), key: "in", numeric: true },
                { header: t("jr.report_col_out"), key: "out", numeric: true },
              ]}
              rows={detail.lines.map((l) => ({ account: `${l.name ?? l.kind} (${l.currency})`, in: l.natural.startsWith("-") ? "-" : fmt(l.natural), out: l.natural.startsWith("-") ? fmt(l.natural.slice(1)) : "-" }))}
              summary={[{ label: t("jr.report_col_item"), value: itemOf(t, detail.type, detail.params) }, ...(detail.note ? [{ label: t("jr.field.note"), value: detail.note }] : [])]}
            />
          </div>,
          document.body,
        )}
    </>
  );
}
